import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useColorModeValue,
  useStyleConfig,
} from "@chakra-ui/react";
import { RiArrowDownSLine } from "@remixicon/react";
import { useMemo } from "react";
import { Link } from "react-router";

import { HeaderLayoutHeading } from "@/common/components/HeaderLayout";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import MenuDescription from "@/common/components/MenuDescription";
import MotionBox from "@/common/components/MotionBox";
import { NavLink } from "@/common/components/NavLink";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { SegmentSizeIndicator } from "@/company/components/SegmentSizeIndicator";
import { useSegmentCounts, useSegments } from "@/company/data/useSegments";
import { useFlag } from "@reflag/react-sdk";
import { SegmentDTO } from "@reflag/shared/segmentAPI";
import { GlobalSettingsUrl, SegmentUrl } from "@reflag/shared/urls";

import { SegmentViewType } from "./SegmentViewToggle";

export function SegmentNavigator({
  currentSegment,
  viewType = "companies",
}: {
  currentSegment: SegmentDTO;
  viewType?: SegmentViewType;
}) {
  const { env } = useEnvironment();
  const { isEnabled: segmentCountsDisabled } = useFlag(
    "disable-segment-counts",
  );

  const { data: segments = [], isLoading: isLoadingSegments } = useSegments();
  const { data: segmentCounts = [] } = useSegmentCounts({
    enabled: !segmentCountsDisabled,
  });
  const segmentCountsById = useMemo(
    () => new Map(segmentCounts.map((count) => [count.segmentId, count])),
    [segmentCounts],
  );

  const segmentsMenu = segments
    .sort((a, b) =>
      a.isAllSegment ? -1 : b.isAllSegment ? 1 : a.name.localeCompare(b.name),
    )
    .map((segment) => {
      return {
        id: segment.id,
        to: SegmentUrl(env, { id: segment.id, name: segment.name }),
        label: segment.name,
        count: segmentCountsById.get(segment.id)?.segmentCount,
      };
    });

  const currentItemColor = useColorModeValue("brand.500", "brand.300");
  const currentSegmentName = getCurrentSegmentName(currentSegment, viewType);

  return (
    <Menu>
      <MenuButton
        aria-label="Switch segment"
        as={Button}
        display="flex"
        pl={2}
        pr={1}
        rightIcon={
          <Box fontSize="lg" ml={-0.5}>
            <RiArrowDownSLine size={16} />
          </Box>
        }
        size="sm"
        variant="outlineOnHover"
      >
        <HStack spacing={1.5}>
          <SegmentSizeIndicator segmentId={currentSegment.id} size="16px" />
          <HeaderLayoutHeading fontSize="lg">
            {currentSegmentName}
          </HeaderLayoutHeading>
        </HStack>
      </MenuButton>
      <MenuList>
        <HStack fontSize="sm">
          <MenuDescription maxW="xs" pr={0}>
            Switch segment
          </MenuDescription>
          <InfoIconTooltip
            docsURL="https://docs.reflag.com/product-handbook/concepts/segment"
            text="Segments are filtered lists of companies. Column configuration are saved per segment."
          />
        </HStack>
        <MenuDivider my={0} />
        {isLoadingSegments ? (
          <Box pb={1} pt={2} px={3}>
            <Spinner size="xs" variant="subtle" />
          </Box>
        ) : (
          segmentsMenu.map((i) => (
            <MenuItem
              key={i.id}
              as={NavLink}
              icon={<SegmentSizeIndicator segmentId={i.id} size="15px" />}
              matchSearchParams={["segment"]}
              sx={{
                "&[aria-current='page']": {
                  color: currentItemColor,
                },
              }}
              to={i.to}
            >
              <HStack>
                <Text flexGrow={1}>{i.label}</Text>
                <Text color="dimmed">
                  {!segmentCountsDisabled && i.count != null && (
                    <MotionBox
                      animate={{ opacity: 1, translateX: 0 }}
                      as="span"
                      initial={{ opacity: 0, translateX: -3 }}
                      transition={{ duration: "0.3", ease: "easeOut" }}
                    >
                      {i.count}
                    </MotionBox>
                  )}
                </Text>
              </HStack>
            </MenuItem>
          ))
        )}
        <MenuDivider my={0} />

        <MenuItem
          as={Link}
          display="block"
          m="2"
          sx={useStyleConfig("Button", {
            variant: "outline",
            size: "xs",
          })}
          to={GlobalSettingsUrl(env, "app-segments")}
          width="max-content"
        >
          Manage segments
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

function getCurrentSegmentName(segment: SegmentDTO, viewType: SegmentViewType) {
  if (!segment.isAllSegment) {
    return segment.name;
  }

  return viewType === "users" ? "All users" : "All companies";
}
